import { useEffect, useMemo, useState } from 'react';
import MentorshipCampusConfig, { MentorshipCampusMixedFields } from './MentorshipCampusConfig';
import MentorshipService, {
    emptyCampusConfigPayload,
    MentorshipCampusConfig as MentorshipCampusConfigModel,
    MentorshipCampusConfigPayload,
    MentorshipProgramOption,
} from './MentorshipService';

interface MentorshipAllCampusesProps {
    programs: MentorshipProgramOption[];
    associatedCampusIds: number[];
    configs: MentorshipCampusConfigModel[];
    disabled?: boolean;
    saving?: boolean;
    onSave: (payload: MentorshipCampusConfigPayload) => void;
    onSaveMerged: (updates: { campusId: number; payload: MentorshipCampusConfigPayload }[]) => void;
}

const EMPTY_MIXED: MentorshipCampusMixedFields = {
    mentorExperienceThreshold: false,
    maxMenteesPerMentor: false,
    menteeTarget: false,
    eligiblePrograms: false,
    menteeMode: false,
    allowOptOutMidCohort: false,
    allowMentorChangeRequest: false,
};

const programsSignature = (payload: MentorshipCampusConfigPayload) =>
    JSON.stringify(
        MentorshipService.normalizeEligiblePrograms(payload.eligiblePrograms)
            .map((item) => ({
                programId: item.programId,
                years: [...item.years].sort((a, b) => a - b),
            }))
            .sort((a, b) => a.programId - b.programId)
    );

const payloadFromStored = (config?: MentorshipCampusConfigModel | null): MentorshipCampusConfigPayload => {
    if (!config || config.id == null) return emptyCampusConfigPayload();
    return {
        mentorExperienceThreshold: config.mentorExperienceThreshold ?? null,
        maxMenteesPerMentor: config.maxMenteesPerMentor ?? null,
        menteeTarget: config.menteeTarget ?? null,
        eligiblePrograms: MentorshipService.normalizeEligiblePrograms(config.eligiblePrograms),
        menteeMode: config.menteeMode ?? null,
        allowOptOutMidCohort: MentorshipService.asBoolean(config.allowOptOutMidCohort),
        allowMentorChangeRequest: MentorshipService.asBoolean(config.allowMentorChangeRequest),
    };
};

const sameScalar = (left: string | number | null, right: string | number | null) => (left ?? null) === (right ?? null);

const composeAllCampuses = (
    associatedCampusIds: number[],
    configs: MentorshipCampusConfigModel[]
): {
    payload: MentorshipCampusConfigPayload;
    mixed: MentorshipCampusMixedFields;
    eligibleAlumniCount: number | null;
    eligibleStudentCount: number | null;
} => {
    const byCampus = new Map(configs.map((row) => [Number(row.campusId), row]));
    const storedRows = associatedCampusIds.map((campusId) => byCampus.get(Number(campusId)) || null);
    const payloads = storedRows.map((row) => payloadFromStored(row));
    if (payloads.length === 0) {
        return {
            payload: emptyCampusConfigPayload(),
            mixed: { ...EMPTY_MIXED },
            eligibleAlumniCount: null,
            eligibleStudentCount: null,
        };
    }

    const first = payloads[0];
    const mixed: MentorshipCampusMixedFields = {
        mentorExperienceThreshold: !payloads.every((item) =>
            sameScalar(item.mentorExperienceThreshold, first.mentorExperienceThreshold)
        ),
        maxMenteesPerMentor: !payloads.every((item) => sameScalar(item.maxMenteesPerMentor, first.maxMenteesPerMentor)),
        menteeTarget: !payloads.every((item) => sameScalar(item.menteeTarget, first.menteeTarget)),
        eligiblePrograms: !payloads.every((item) => programsSignature(item) === programsSignature(first)),
        menteeMode: !payloads.every((item) => sameScalar(item.menteeMode, first.menteeMode)),
        allowOptOutMidCohort: !payloads.every(
            (item) => MentorshipService.asBoolean(item.allowOptOutMidCohort) === MentorshipService.asBoolean(first.allowOptOutMidCohort)
        ),
        allowMentorChangeRequest: !payloads.every(
            (item) =>
                MentorshipService.asBoolean(item.allowMentorChangeRequest) ===
                MentorshipService.asBoolean(first.allowMentorChangeRequest)
        ),
    };

    const empty = emptyCampusConfigPayload();
    return {
        payload: {
            mentorExperienceThreshold: mixed.mentorExperienceThreshold ? empty.mentorExperienceThreshold : first.mentorExperienceThreshold,
            maxMenteesPerMentor: mixed.maxMenteesPerMentor ? empty.maxMenteesPerMentor : first.maxMenteesPerMentor,
            menteeTarget: mixed.menteeTarget ? empty.menteeTarget : first.menteeTarget,
            eligiblePrograms: mixed.eligiblePrograms ? empty.eligiblePrograms : first.eligiblePrograms,
            menteeMode: mixed.menteeMode ? empty.menteeMode : first.menteeMode,
            allowOptOutMidCohort: mixed.allowOptOutMidCohort ? empty.allowOptOutMidCohort : first.allowOptOutMidCohort,
            allowMentorChangeRequest: mixed.allowMentorChangeRequest
                ? empty.allowMentorChangeRequest
                : first.allowMentorChangeRequest,
        },
        mixed,
        eligibleAlumniCount: storedRows[0]?.eligibleAlumniCount ?? null,
        eligibleStudentCount: storedRows[0]?.eligibleStudentCount ?? null,
    };
};

const mergeCampusPayload = (
    stored: MentorshipCampusConfigModel | null,
    draft: MentorshipCampusConfigPayload,
    mixed: MentorshipCampusMixedFields
): MentorshipCampusConfigPayload => {
    const current = payloadFromStored(stored);
    return {
        mentorExperienceThreshold: mixed.mentorExperienceThreshold
            ? current.mentorExperienceThreshold
            : draft.mentorExperienceThreshold,
        maxMenteesPerMentor: mixed.maxMenteesPerMentor ? current.maxMenteesPerMentor : draft.maxMenteesPerMentor,
        menteeTarget: mixed.menteeTarget ? current.menteeTarget : draft.menteeTarget,
        eligiblePrograms: mixed.eligiblePrograms ? current.eligiblePrograms : draft.eligiblePrograms,
        menteeMode: mixed.menteeMode ? current.menteeMode : draft.menteeMode,
        allowOptOutMidCohort: mixed.allowOptOutMidCohort ? current.allowOptOutMidCohort : draft.allowOptOutMidCohort,
        allowMentorChangeRequest: mixed.allowMentorChangeRequest
            ? current.allowMentorChangeRequest
            : draft.allowMentorChangeRequest,
    };
};

const mixedSnapshot = (mixed: MentorshipCampusMixedFields) => JSON.stringify({ ...EMPTY_MIXED, ...mixed });

const MentorshipAllCampuses = ({
    programs,
    associatedCampusIds,
    configs,
    disabled = false,
    saving = false,
    onSave,
    onSaveMerged,
}: MentorshipAllCampusesProps) => {
    const composeKey = JSON.stringify({
        ids: associatedCampusIds,
        rows: associatedCampusIds.map((campusId) => {
            const row = configs.find((item) => Number(item.campusId) === Number(campusId)) || null;
            return {
                ...payloadFromStored(row),
                eligibleAlumniCount: row?.eligibleAlumniCount ?? null,
                eligibleStudentCount: row?.eligibleStudentCount ?? null,
            };
        }),
    });
    const composed = useMemo(
        () => composeAllCampuses(associatedCampusIds, configs),
        // composeKey captures associated ids + stored payloads
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [composeKey]
    );
    const [draft, setDraft] = useState<MentorshipCampusConfigPayload>(composed.payload);
    const [mixed, setMixed] = useState<MentorshipCampusMixedFields>(composed.mixed);
    const [baseline, setBaseline] = useState(composed);

    useEffect(() => {
        setDraft(composed.payload);
        setMixed(composed.mixed);
        setBaseline(composed);
    }, [composed]);

    const displayConfig = useMemo(
        () => ({
            ...composed.payload,
            eligibleAlumniCount: composed.eligibleAlumniCount,
            eligibleStudentCount: composed.eligibleStudentCount,
        }),
        [composed]
    );

    const dirty =
        JSON.stringify(draft) !== JSON.stringify(baseline.payload) || mixedSnapshot(mixed) !== mixedSnapshot(baseline.mixed);
    const saveDisabled = !dirty;

    const handleChange = (
        next: MentorshipCampusConfigPayload,
        changedKeys: (keyof MentorshipCampusConfigPayload)[] = []
    ) => {
        if (changedKeys.length > 0) {
            setMixed((current) => {
                const nextMixed = { ...current };
                changedKeys.forEach((key) => {
                    if (key in nextMixed) {
                        (nextMixed as Record<string, boolean | undefined>)[key] = false;
                    }
                });
                return nextMixed;
            });
        }
        setDraft(next);
    };

    const handleSave = (payload: MentorshipCampusConfigPayload) => {
        if (saveDisabled) return;
        const anyMixed = Object.values(mixed).some(Boolean);
        if (!anyMixed) {
            onSave(payload);
            return;
        }
        const byCampus = new Map(configs.map((row) => [Number(row.campusId), row]));
        onSaveMerged(
            associatedCampusIds.map((campusId) => ({
                campusId,
                payload: mergeCampusPayload(byCampus.get(Number(campusId)) || null, payload, mixed),
            }))
        );
    };

    return (
        <div className="card border-0 rounded-3 shadow-sm bg-white mb-3">
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <div>
                    <div className="font-14 font-700 text-dark">🏛️ All Campus</div>
                    <div className="font-11 text-secondary">Saving applies these values to every campus associated with this cohort.</div>
                </div>
                <span className="badge bg-success-subtle text-success-emphasis rounded-pill d-inline-flex align-items-center font-12 fw-medium px-2.5 py-1">
                    <svg width="6" height="6" className="me-1">
                        <circle cx="3" cy="3" r="3" fill="#10B981"></circle>
                    </svg>
                    Active
                </span>
            </div>

            <div className="card-body p-4">
                <MentorshipCampusConfig
                    key={composeKey}
                    idPrefix="mentorship-all-campuses"
                    config={displayConfig}
                    programs={programs}
                    disabled={disabled}
                    saving={saving}
                    showSave={true}
                    saveDisabled={saveDisabled}
                    mixedFields={mixed}
                    onChange={handleChange}
                    onSave={handleSave}
                />
            </div>
        </div>
    );
};

export default MentorshipAllCampuses;
